import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { sendAssignmentUpdateNotification } from '../services/emailNotifications';
import ProjectDetailWorkspace from '../components/projects/ProjectDetailWorkspace';
import {
  ensureProjectExtensions,
  getBudgetPercentUsed,
  getProfitHealthVariant,
} from '../utils/projectUtils';
import { canMutateProjects, canViewProjects } from '../utils/entitlements';
import { runSupabaseAuthedOperation, toUserMessage } from '../utils/asyncReliability';
import { DashboardPageHeader, dashboardBtnPrimary, dashboardBtnModalCancel, dashboardModalPanelMd, dashboardInputClass, dashboardLabelClass } from '../components/dashboard/DashboardChrome';
import { devError } from '../utils/logger';

const MODAL_Z = 'z-[10050]';

const STATUS_BADGE_CLASS = {
  pending: 'bg-amber-100 text-amber-900',
  active: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-blue-100 text-blue-800',
};

const STATUS_PILLS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'completed', label: 'Completed' },
];

const STATUS_DISPLAY_LABELS = {
  pending: 'Pending',
  active: 'Active',
  completed: 'Completed',
};

function getDateStatus(project) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = project.startDate ? new Date(project.startDate) : null;
  const end = project.deadline ? new Date(project.deadline) : null;
  if (start && today < start) return 'pending';
  if (end && today > end) return 'completed';
  return 'active';
}

function deepCleanForStorage(obj) {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (obj instanceof Date) {
    const t = obj.getTime();
    return Number.isNaN(t) ? null : obj.toISOString();
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => deepCleanForStorage(item)).filter((item) => item !== null);
  }
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) {
      const cleanedValue = deepCleanForStorage(value);
      if (cleanedValue !== null) cleaned[key] = cleanedValue;
    }
  }
  return cleaned;
}

function BudgetStrip({ percent, variant, budget, spent }) {
  const w = Math.min(100, percent);
  const barClass =
    variant === 'danger' ? 'bg-red-500' : variant === 'warn' ? 'bg-amber-400' : 'bg-emerald-500';
  const remaining = Math.max(0, (parseFloat(budget) || 0) - spent);
  return (
    <div className="mt-4">
      <div className="flex justify-between items-baseline gap-2 text-[11px]">
        <span className="text-slate-500">Budget</span>
        <span className={variant === 'danger' ? 'font-semibold text-red-600 tabular-nums' : 'text-slate-600 tabular-nums'}>
          {percent.toFixed(0)}% used · ${remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })} left
        </span>
      </div>
      <div className="h-1.5 mt-1.5 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barClass}`} style={{ width: `${w}%` }} />
      </div>
    </div>
  );
}

const parseProjectDate = (d) => {
  if (!d) return null;
  if (typeof d.toDate === 'function') return d.toDate();
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? null : x;
};

const formatProjectDate = (d) => {
  const x = parseProjectDate(d);
  return x ? x.toLocaleDateString() : '';
};

const projectSortTime = (p) => {
  const end = parseProjectDate(p.deadline);
  const created = p.createdAt ? parseProjectDate(p.createdAt) : null;
  const t = end || created;
  return t ? t.getTime() : 0;
};

/**
 * Standalone Projects area (Pro): full project CRUD, expense links, and assignment sync.
 * Data lives on the user document (`projects`, `dayAssignments`, etc.) — same as Scheduling.
 */
const Projects = ({
  /** When embedded in Dashboard, pass Supabase auth user so we do not block on getSession() (can hang intermittently). */
  authUser: authUserProp = null,
  projects = [],
  setProjects,
  expenses = [],
  saveExpense,
  deleteExpense,
  linkedTeamMembers = [],
  linkedSubcontractors = [],
  projectsAccessMode = 'full',
  addedByName = null,
}) => {
  const [user, setUser] = useState(() => authUserProp || null);
  const [loading, setLoading] = useState(() => !authUserProp);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [teams, setTeams] = useState([]);
  const [clients, setClients] = useState([]);
  const [allDayAssignments, setAllDayAssignments] = useState({});
  const [resolvedAccessMode, setResolvedAccessMode] = useState(projectsAccessMode);
  const [listFilterQuery, setListFilterQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pageSize, setPageSize] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailProjectId, setDetailProjectId] = useState(null);
  const loadUserDataRef = useRef(async () => {});

  const navigate = useNavigate();
  const isReadOnly = resolvedAccessMode !== 'full';

  const handleRestrictedAction = () => {
    alert('Upgrade to Pro to create and edit projects');
    navigate('/dashboard/settings?plans=1');
  };

  const runAuthedWrite = async (name, fn, timeoutMs = 50000) => {
    const result = await runSupabaseAuthedOperation({
      name,
      supabaseClient: supabase,
      timeoutMs,
      retries: 1,
      fn,
    });
    if (!result.ok) {
      throw result.error || new Error(result.userMessage || 'Could not save changes.');
    }
    return result.data;
  };

  useEffect(() => {
    let cancelled = false;

    const applySession = async (session, authEvent) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setLoading(false);

      if (authEvent === 'TOKEN_REFRESHED' || authEvent === 'USER_UPDATED') {
        return;
      }

      if (!currentUser) {
        if (authEvent === 'SIGNED_OUT') {
          navigate('/', { replace: true });
        } else {
          navigate('/account', { replace: true });
        }
        return;
      }

      try {
        const { data: userData, error } = await supabase.from('profiles').select('*').eq('id', currentUser.id).single();
        if (cancelled) return;
        if (error) throw error;
        if (userData) {
          if (!canViewProjects(userData)) {
            navigate('/dashboard');
            return;
          }
          setResolvedAccessMode(canMutateProjects(userData) ? 'full' : 'read_only');
          await loadUserDataRef.current(currentUser.id);
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        if (!cancelled) {
          devError('Error checking user plan:', error);
          navigate('/dashboard');
        }
      }
    };

    let sessionWaitTimeoutId;
    if (authUserProp) {
      applySession({ user: authUserProp }, 'INITIAL_SESSION');
    } else {
      sessionWaitTimeoutId = setTimeout(() => {
        if (!cancelled) setLoading(false);
      }, 15000);
      supabase.auth
        .getSession()
        .then(({ data: { session } }) => {
          if (!cancelled) applySession(session, 'INITIAL_SESSION');
        })
        .catch(() => {
          if (!cancelled) setLoading(false);
        })
        .finally(() => {
          if (sessionWaitTimeoutId) clearTimeout(sessionWaitTimeoutId);
        });
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (authUserProp && event === 'INITIAL_SESSION') return;
      applySession(session, event);
    });

    return () => {
      cancelled = true;
      if (sessionWaitTimeoutId) clearTimeout(sessionWaitTimeoutId);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount auth listener once; `applySession` closes over latest `authUserProp` via INITIAL_SESSION guard only
  }, [navigate, authUserProp?.id]);

  useEffect(() => {
    setResolvedAccessMode(projectsAccessMode);
  }, [projectsAccessMode]);

  useEffect(() => {
    setCurrentPage(1);
  }, [listFilterQuery, statusFilter, pageSize]);

  const filteredSortedProjects = useMemo(() => {
    const q = listFilterQuery.trim().toLowerCase();
    let list = projects.map(ensureProjectExtensions).filter((p) => {
      if (!q) return true;
      const name = (p.name || '').toLowerCase();
      const client = (p.client || '').toLowerCase();
      return name.includes(q) || client.includes(q);
    });
    if (statusFilter !== 'all') {
      list = list.filter((p) => getDateStatus(p) === statusFilter);
    }
    return list.sort((a, b) => projectSortTime(b) - projectSortTime(a));
  }, [projects, listFilterQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredSortedProjects.length / pageSize));

  const paginatedProjects = useMemo(() => {
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * pageSize;
    return filteredSortedProjects.slice(start, start + pageSize);
  }, [filteredSortedProjects, currentPage, pageSize, totalPages]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const loadUserData = async (userId) => {
    try {
      const { data: userData, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
      if (error) throw error;
      if (userData) {
        const nextProjects = userData.projects || [];
        const nextTeams = userData.teams || [];
        const nextAssignments = userData.day_assignments || userData.dayAssignments || {};

        setProjects((nextProjects || []).map(ensureProjectExtensions));
        setTeams(nextTeams);
        setClients(userData.clients || []);
        setAllDayAssignments(nextAssignments);

        await cleanupOrphanedAssignments(userId, nextAssignments, nextProjects, nextTeams);
      }
    } catch (error) {
      devError('Error loading user data:', error);
    }
  };

  loadUserDataRef.current = loadUserData;

  const cleanupOrphanedAssignments = async (
    userId,
    assignmentsMap = allDayAssignments,
    projectsList = projects,
    teamsList = teams
  ) => {
    try {
      if (!userId) return;

      const updatedAssignments = {};
      Object.keys(assignmentsMap).forEach((dateKey) => {
        const assignments = assignmentsMap[dateKey];
        const validAssignments = assignments.filter((assignment) => {
          const teamMemberExists = teamsList.some((team) => team.id === assignment.memberId);
          if (!teamMemberExists) return false;
          if (assignment.assignmentType === 'project') {
            return projectsList.some((project) => project.id === assignment.projectId);
          }
          return true;
        });
        if (validAssignments.length > 0) {
          updatedAssignments[dateKey] = validAssignments;
        }
      });

      await runAuthedWrite('projects:cleanupOrphanedAssignments', async () => {
        const { error: assignErr } = await supabase
          .from('profiles')
          .update({ day_assignments: updatedAssignments })
          .eq('id', userId);
        if (assignErr) throw assignErr;
      });
      setAllDayAssignments(updatedAssignments);
    } catch (error) {
      devError('Error cleaning up assignments:', error);
    }
  };

  const saveProjectsList = async (updatedItems) => {
    if (isReadOnly) throw new Error('PRO_REQUIRED');
    if (!user?.id) throw new Error('No user');
    if (typeof setProjects !== 'function') throw new Error('Projects state is not connected');
    const normalized = updatedItems.map(ensureProjectExtensions);
    const cleanedItems = deepCleanForStorage(normalized);
    await runAuthedWrite('projects:saveProjectsList', async () => {
      const { error } = await supabase.from('profiles').update({ projects: cleanedItems }).eq('id', user.id);
      if (error) {
        devError('[saveProjectsList] Supabase error:', error);
        throw new Error(error.message || error.details || 'Could not save projects to your profile');
      }
    });
    setProjects(normalized);
  };

  const patchProject = async (projectId, updater) => {
    try {
      const updatedItems = projects.map((p) => {
        const base = ensureProjectExtensions(p);
        if (p.id !== projectId) return base;
        return ensureProjectExtensions(updater(base));
      });
      await saveProjectsList(updatedItems);
    } catch (e) {
      devError('Error patching project:', e);
      const msg = e?.message || String(e);
      alert(msg ? `Failed to update project: ${msg}` : 'Failed to update project.');
    }
  };

  const handleAddItem = async () => {
    if (isReadOnly) {
      handleRestrictedAction();
      return;
    }
    if (!formData.name || !formData.name.trim()) {
      alert('Please enter a name');
      return;
    }

    setIsSubmitting(true);
    try {
      let updatedItems;

      if (editingItem) {
        const currentItems = projects;
        const cleanFormData = Object.fromEntries(
          Object.entries(formData).filter(([, value]) => value !== undefined && value !== null)
        );
        updatedItems = currentItems.map((item) => {
          if (item.id !== editingItem.id) return item;
          const merged = {
            ...item,
            ...cleanFormData,
            name: formData.name.trim(),
            projectStatus: formData.projectStatus || item.projectStatus || 'active',
          };
          delete merged.weekendDays;
          return merged;
        });
      } else {
        const cleanFormData = Object.fromEntries(
          Object.entries(formData).filter(([, value]) => value !== undefined && value !== null)
        );
        const newItem = {
          id: Date.now().toString(),
          name: formData.name.trim(),
          ...cleanFormData,
          projectStatus: formData.projectStatus || 'active',
          createdAt: new Date().toISOString(),
        };
        delete newItem.weekendDays;
        updatedItems = [newItem, ...projects];
      }

      await saveProjectsList(updatedItems);

      if (editingItem) {
        const updatedProject = updatedItems.find((p) => p.id === editingItem.id);
        if (updatedProject) {
          await updateProjectAssignments(editingItem.id, updatedProject);
        }
      }

      setFormData({});
      setEditingItem(null);
      setShowAddModal(false);
      setClientSearch('');
      setShowClientDropdown(false);
    } catch (error) {
      devError('Error saving project:', error);
      const msg = error?.message || String(error);
      alert(msg ? `Failed to save project: ${msg}` : 'Failed to save project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateProjectAssignments = async (projectId, updatedProject) => {
    try {
      const newStartDate = updatedProject.startDate?.toDate
        ? updatedProject.startDate.toDate()
        : new Date(updatedProject.startDate);
      const newEndDate = updatedProject.deadline?.toDate
        ? updatedProject.deadline.toDate()
        : new Date(updatedProject.deadline);

      const updatedAllDayAssignments = { ...allDayAssignments };
      let hasChanges = false;

      Object.keys(updatedAllDayAssignments).forEach((dateKey) => {
        if (updatedAllDayAssignments[dateKey]) {
          updatedAllDayAssignments[dateKey] = updatedAllDayAssignments[dateKey].map((assignment) => {
            if (assignment.projectId === projectId && assignment.assignmentType === 'project') {
              hasChanges = true;
              return {
                ...assignment,
                startDate: newStartDate,
                endDate: newEndDate,
                projectName: updatedProject.name,
              };
            }
            return assignment;
          });
        }
      });

      if (hasChanges) {
        await runAuthedWrite('projects:updateProjectAssignments', async () => {
          const { error: daErr } = await supabase
            .from('profiles')
            .update({ day_assignments: updatedAllDayAssignments })
            .eq('id', user.id);
          if (daErr) throw daErr;
        });
        setAllDayAssignments(updatedAllDayAssignments);

        try {
          const affectedTeamMembers = new Set();
          Object.values(updatedAllDayAssignments)
            .flat()
            .forEach((assignment) => {
              if (assignment.projectId === projectId && assignment.assignmentType === 'project') {
                affectedTeamMembers.add(assignment.memberId);
              }
            });

          for (const memberId of affectedTeamMembers) {
            const teamMember = teams.find((t) => t.id === memberId);
            if (teamMember && teamMember.email) {
              const assignmentData = {
                startDate: newStartDate,
                endDate: newEndDate,
                workDays: [],
                notes: '',
              };
              await sendAssignmentUpdateNotification(assignmentData, teamMember, updatedProject, true);
            }
          }
        } catch (emailError) {
          devError('Error sending project update notifications:', emailError);
        }
      }
    } catch (error) {
      devError('Error updating project assignments:', error);
      alert(toUserMessage(error, 'Failed to update project assignments. Please try again.'));
    }
  };

  const handleConfirmDelete = async () => {
    if (isReadOnly) {
      handleRestrictedAction();
      return;
    }
    if (!itemToDelete) return;

    try {
      const updatedItems = projects.filter((item) => item.id !== itemToDelete.id);

      await saveProjectsList(updatedItems);

      if (itemToDelete.id === detailProjectId) {
        setDetailProjectId(null);
      }

      const updatedAllDayAssignments = { ...allDayAssignments };
      let hasChanges = false;

      Object.keys(updatedAllDayAssignments).forEach((dateKey) => {
        const originalLength = updatedAllDayAssignments[dateKey].length;
        updatedAllDayAssignments[dateKey] = updatedAllDayAssignments[dateKey].filter(
          (assignment) => assignment.projectId !== itemToDelete.id
        );
        if (updatedAllDayAssignments[dateKey].length !== originalLength) {
          hasChanges = true;
        }
        if (updatedAllDayAssignments[dateKey].length === 0) {
          delete updatedAllDayAssignments[dateKey];
        }
      });

      if (hasChanges) {
        await runAuthedWrite('projects:deleteProjectAssignments', async () => {
          const { error: delDaErr } = await supabase
            .from('profiles')
            .update({ day_assignments: updatedAllDayAssignments })
            .eq('id', user.id);
          if (delDaErr) throw delDaErr;
        });
        setAllDayAssignments(updatedAllDayAssignments);
      }

      await cleanupOrphanedAssignments(user.id, updatedAllDayAssignments, updatedItems, teams);

      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      devError('Error deleting project:', error);
      alert(toUserMessage(error, 'Failed to delete project. Please try again.'));
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleEditItem = (item) => {
    if (isReadOnly) {
      handleRestrictedAction();
      return;
    }
    setFormData(ensureProjectExtensions(item));
    setEditingItem(item);
    setShowAddModal(true);

    if (item.client) {
      setClientSearch(item.client);
    } else {
      setClientSearch('');
    }
  };

  const getProjectTotalExpenses = (projectId) =>
    expenses
      .filter((expense) => expense.projectId === projectId)
      .reduce((total, expense) => total + (parseFloat(expense.amount) || 0), 0);

  const handleDeleteItem = (itemId) => {
    setItemToDelete({ id: itemId, type: 'project' });
    setShowDeleteModal(true);
  };

  const getModalFields = () => [
    { name: 'name', label: 'Project Name', type: 'text', required: true },
    { name: 'client', label: 'Client', type: 'searchable-client', required: true },
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'deadline', label: 'Deadline', type: 'date', required: false },
    { name: 'budget', label: 'Budget', type: 'number', required: false },
    { name: 'description', label: 'Description', type: 'textarea', required: false },
  ];

  const renderModal = () => {
    if (!showAddModal) return null;

    return createPortal(
      <div className={`fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center ${MODAL_Z} p-4`}>
        <div className={`${dashboardModalPanelMd} p-6 sm:p-8 max-h-[90vh] overflow-y-auto`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900 tracking-tight dark:text-slate-100">
              {editingItem ? 'Edit project' : 'New project'}
            </h3>
            <button
              onClick={() => {
                setShowAddModal(false);
                setFormData({});
                setEditingItem(null);
                setClientSearch('');
                setShowClientDropdown(false);
              }}
              className="text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
              type="button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddItem();
            }}
          >
            <div className="space-y-4">
              {getModalFields().map((field) => (
                <div key={field.name}>
                  <label className={`${dashboardLabelClass} mb-1`}>{field.label}</label>
                  {field.type === 'select' ? (
                    <select
                      value={formData[field.name] ?? (field.name === 'projectStatus' ? 'active' : '')}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className={dashboardInputClass}
                      required={field.required}
                    >
                      {!field.required && <option value="">Select {field.label}</option>}
                      {(field.options || []).map((option) => (
                        <option key={option} value={option}>
                          {(field.optionLabels && field.optionLabels[option]) || option}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className={dashboardInputClass}
                      rows="3"
                      required={field.required}
                    />
                  ) : field.type === 'searchable-client' ? (
                    <div className="relative">
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => setShowClientDropdown(true)}
                        onBlur={() => {
                          setTimeout(() => setShowClientDropdown(false), 200);
                        }}
                        placeholder="Search for a client..."
                        className={dashboardInputClass}
                        required={field.required}
                      />
                      {showClientDropdown && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto dark:border-slate-600 dark:bg-slate-800">
                          {clients
                            .filter(
                              (client) =>
                                client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                                client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                            )
                            .map((client) => (
                              <div
                                key={client.id}
                                onClick={() => {
                                  setFormData({ ...formData, [field.name]: client.name });
                                  setClientSearch(client.name);
                                  setShowClientDropdown(false);
                                }}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 dark:hover:bg-slate-700 dark:border-slate-700"
                              >
                                <div className="font-medium text-gray-900 dark:text-slate-100">{client.name}</div>
                                {client.email && <div className="text-xs text-gray-500 dark:text-slate-400">{client.email}</div>}
                              </div>
                            ))}
                          {clients.filter(
                            (client) =>
                              client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                              client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                          ).length === 0 && (
                            <div className="px-3 py-2 text-gray-500 text-sm">No clients found</div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <input
                      type={field.type}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className={dashboardInputClass}
                      required={field.required}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({});
                  setEditingItem(null);
                  setClientSearch('');
                  setShowClientDropdown(false);
                }}
                className={`${dashboardBtnModalCancel} flex-1 justify-center py-2.5`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (editingItem ? 'Saving...' : 'Adding...') : editingItem ? 'Save Changes' : 'Add Project'}
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body
    );
  };

  const openNewProject = () => {
    if (isReadOnly) {
      handleRestrictedAction();
      return;
    }
    setFormData({ projectStatus: 'active' });
    setEditingItem(null);
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scheduling-section max-w-7xl mx-auto space-y-8 pb-10">
      <DashboardPageHeader
        eyebrow={isReadOnly ? 'Read-only · Projects' : 'Pro · Jobs'}
        title="Projects"
        description="One place for budgets, change orders, site logs, and documents — without the clutter."
        action={
          !isReadOnly ? (
            <button
              onClick={openNewProject}
              className={`${dashboardBtnPrimary} shrink-0 rounded-2xl px-5 py-2.5 shadow-lg sm:py-3`}
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New project
            </button>
          ) : null
        }
      />

      {isReadOnly && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Upgrade to Pro to create and edit projects
        </div>
      )}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative flex-1 min-w-0 max-w-xl">
          <svg
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            value={listFilterQuery}
            onChange={(e) => setListFilterQuery(e.target.value)}
            placeholder="Search projects or clients…"
            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm shadow-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary/25 focus:border-primary outline-none transition-shadow"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide mr-1 hidden sm:inline">Status</span>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_PILLS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setStatusFilter(p.id)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
                  statusFilter === p.id
                    ? 'bg-primary text-white shadow-md ring-2 ring-primary/30'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="hidden sm:block w-px h-6 bg-slate-200 mx-1" />
          <label className="sr-only" htmlFor="project-page-size">
            Per page
          </label>
          <select
            id="project-page-size"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 focus:ring-2 focus:ring-primary/25 outline-none"
          >
            <option value={12}>12 / page</option>
            <option value={24}>24 / page</option>
            <option value={36}>36 / page</option>
          </select>
        </div>
      </div>

      {projects.length > 0 && (
        <p className="text-xs text-slate-500">
          {filteredSortedProjects.length === 0
            ? 'No projects match your search or filters.'
            : `Showing ${(Math.min(currentPage, totalPages) - 1) * pageSize + 1}–${Math.min(
                Math.min(currentPage, totalPages) * pageSize,
                filteredSortedProjects.length
              )} of ${filteredSortedProjects.length}`}
        </p>
      )}

        <div className="space-y-8">
          {projects.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-white px-8 py-16 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-100 to-cyan-50 ring-1 ring-slate-200/80 dark:from-slate-800 dark:to-slate-900 dark:ring-slate-600">
                <svg className="h-8 w-8 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h3 className="mt-6 text-xl font-semibold text-slate-900 dark:text-slate-100">No projects yet</h3>
              <p className="mt-2 text-slate-600 max-w-md mx-auto text-sm dark:text-slate-400">Create a project to tie invoices, expenses, and site documentation together.</p>
              {!isReadOnly && (
                <button
                  onClick={openNewProject}
                  className="mt-8 rounded-2xl bg-primary px-8 py-3 text-sm font-semibold text-white hover:bg-primary-dark transition-colors shadow-lg shadow-primary/20"
                  type="button"
                >
                  Create first project
                </button>
              )}
            </div>
          ) : filteredSortedProjects.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center text-slate-600 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
              No projects match your search. Try another keyword or status.
            </div>
          ) : (
            <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch">
            {paginatedProjects.map((project) => {
              const p = ensureProjectExtensions(project);
              const st = getDateStatus(p);
              const badgeClass = STATUS_BADGE_CLASS[st] || STATUS_BADGE_CLASS.active;
              const totalSpent = getProjectTotalExpenses(project.id);
              const budgetNum = parseFloat(project.budget) || 0;
              const pct = budgetNum > 0 ? getBudgetPercentUsed(budgetNum, totalSpent) : 0;
              const healthVariant = budgetNum > 0 ? getProfitHealthVariant(pct) : 'safe';
              const initials = (project.name || '?')
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((w) => w[0])
                .join('')
                .toUpperCase();

              return (
                <div
                  key={project.id}
                  className="group flex flex-col rounded-3xl border border-slate-200/90 bg-white p-5 shadow-sm transition-all hover:shadow-lg hover:border-slate-300/90 hover:-translate-y-0.5"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-800 to-slate-700 text-xs font-bold text-white shadow-inner">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-base font-bold text-slate-900 leading-snug line-clamp-2">{project.name}</h3>
                        {!isReadOnly && <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            onClick={() => handleEditItem(project)}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-primary"
                            title="Edit"
                            type="button"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteItem(project.id)}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                            title="Delete"
                            type="button"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>}
                      </div>
                      <span className={`mt-1.5 inline-flex text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${badgeClass}`}>
                        {STATUS_DISPLAY_LABELS[st] || st}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                    {project.client && (
                      <p className="flex items-center gap-2 text-sm text-slate-700">
                        <svg className="w-4 h-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="truncate">{project.client}</span>
                      </p>
                    )}
                    {(project.startDate || project.deadline) && (
                      <p className="flex items-center gap-2 text-xs text-slate-500">
                        <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {project.startDate && <span>{formatProjectDate(project.startDate)}</span>}
                        {project.startDate && project.deadline && <span className="text-slate-300">→</span>}
                        {project.deadline && <span>{formatProjectDate(project.deadline)}</span>}
                      </p>
                    )}

                    {budgetNum > 0 ? (
                      <>
                        <div className="flex items-baseline justify-between text-xs text-slate-500 pt-1">
                          <span>Est. ${budgetNum.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                          <span className="font-semibold text-slate-800 tabular-nums">
                            ${totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spent
                          </span>
                        </div>
                        <BudgetStrip percent={pct} variant={healthVariant} budget={budgetNum} spent={totalSpent} />
                      </>
                    ) : (
                      <div className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        <span className="font-medium text-slate-700">Spend</span>{' '}
                        <span className="tabular-nums font-semibold">
                          ${totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>

                  {project.description && (
                    <p className="mt-3 line-clamp-2 rounded-xl bg-slate-50/80 px-3 py-2 text-[11px] leading-relaxed text-slate-600 border border-slate-100">
                      {project.description}
                    </p>
                  )}

                  <div className="mt-5 flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => setDetailProjectId(project.id)}
                      className="w-full rounded-2xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-primary-dark transition-colors shadow-sm shadow-primary/25"
                    >
                      Open workspace
                    </button>
                    {isReadOnly && (
                      <button
                        type="button"
                        onClick={handleRestrictedAction}
                        className="w-full rounded-2xl border border-amber-300 bg-amber-50 py-2.5 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100"
                      >
                        Upgrade to edit
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-200/80">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={Math.min(currentPage, totalPages) <= 1}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="text-sm font-medium text-slate-500">
                Page {Math.min(currentPage, totalPages)} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={Math.min(currentPage, totalPages) >= totalPages}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          )}
            </>
          )}
        </div>

        {renderModal()}

        {showDeleteModal &&
          createPortal(
            <div className={`fixed inset-0 bg-black/60 flex items-center justify-center ${MODAL_Z} p-4`}>
              <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Delete Project</h3>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-gray-700">
                    Are you sure you want to delete this <strong>project</strong>?
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    This action cannot be undone. All associated data and assignments will be permanently removed.
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    type="button"
                  >
                    Delete Project
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

      {user &&
        detailProjectId &&
        projects.some((p) => p.id === detailProjectId) && (
          <ProjectDetailWorkspace
            user={user}
            project={projects.find((p) => p.id === detailProjectId)}
            expenses={expenses}
            teams={linkedTeamMembers}
            subcontractors={linkedSubcontractors}
            isReadOnly={isReadOnly}
            onClose={() => setDetailProjectId(null)}
            onPatchProject={patchProject}
            getProjectTotalExpenses={getProjectTotalExpenses}
            saveExpense={saveExpense}
            deleteExpense={deleteExpense}
            addedByName={addedByName}
          />
        )}
    </div>
  );
};

export default Projects;
